import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Save, RefreshCw, GraduationCap, Eye, EyeOff, X } from "lucide-react";
import { API } from "@/lib/auth";

/**
 * Full CRUD editor for course content.
 *   - Left rail: list of courses, hidden badge, sort_order edit inline
 *   - Right pane: form with all editable fields + image uploader + pricing manager
 */
export default function CourseEditor() {
  const [courses, setCourses] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [creating, setCreating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [ensuring, setEnsuring] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/courses`);
      setCourses(r.data || []);
      if (!selectedSlug && r.data?.length) setSelectedSlug(r.data[0].slug);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load courses");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const selected = useMemo(
    () => courses.find((c) => c.slug === selectedSlug) || null,
    [courses, selectedSlug],
  );

  const syncStripe = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/admin/courses/pricing/sync-from-stripe`);
      toast.success(`Synced. ${r.data.courses_updated} course(s) updated from Stripe.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const ensureInStripe = async () => {
    if (!window.confirm(
      "Auto-create every missing course Price in your connected Stripe account?\n\n" +
      "This looks at each pay tier and deposit button, checks if a Stripe Price already exists " +
      "with that lookup key, and creates one if not. Safe to run more than once — nothing is overwritten."
    )) return;
    setEnsuring(true);
    try {
      const r = await axios.post(`${API}/admin/courses/pricing/ensure-in-stripe`);
      const { created_count, already_existed_count, failed_count, created, failed } = r.data || {};
      if (created_count > 0) {
        toast.success(`✓ ${created_count} new Stripe Price(s) created, ${already_existed_count} already existed.`);
      } else if (already_existed_count > 0 && failed_count === 0) {
        toast.success(`Everything already set up — ${already_existed_count} price(s) verified in Stripe.`);
      }
      if (failed_count > 0) {
        console.warn("Stripe price creation failures:", failed);
        toast.error(`${failed_count} price(s) failed — see console. Common cause: your Stripe account is restricted (WIX-managed).`);
      }
      if (created_count > 0) console.info("Created:", created);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reach Stripe — check your API keys");
    } finally {
      setEnsuring(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-12" data-testid="admin-content-panel">
      <div className="lg:col-span-4 space-y-3">
        <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-5">
          <div className="flex items-center justify-between">
            <div className="overline">Courses</div>
            <button
              onClick={() => setCreating(true)}
              data-testid="content-new-course"
              className="btn-pill btn-outline !text-xs !py-1.5"
            >
              <Plus size={12} /> New
            </button>
          </div>
          <div className="mt-4 space-y-1.5">
            {courses.map((c) => (
              <button
                key={c.slug}
                onClick={() => { setSelectedSlug(c.slug); setCreating(false); }}
                data-testid={`content-course-${c.slug}`}
                className={`w-full text-left px-3 py-2 rounded-xl text-sm transition-colors ${
                  selectedSlug === c.slug && !creating
                    ? "bg-[hsl(var(--onyx))] text-[hsl(var(--cream))]"
                    : "hover:bg-blush/40"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate">{c.name}</span>
                  {c.hidden && <EyeOff size={12} className="opacity-60 shrink-0" />}
                </div>
                <div className="text-[10px] mt-0.5 opacity-60 tracking-widest uppercase truncate">
                  {c.slug} · {(c.lookup_keys || []).length} tier{(c.lookup_keys || []).length === 1 ? "" : "s"}
                </div>
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={syncStripe}
          disabled={syncing}
          data-testid="content-sync-stripe"
          className="btn-pill btn-outline w-full !text-xs disabled:opacity-60"
        >
          <RefreshCw size={12} className={syncing ? "animate-spin" : ""} />
          {syncing ? "Syncing…" : "Sync prices from Stripe"}
        </button>

        <button
          onClick={ensureInStripe}
          disabled={ensuring}
          data-testid="content-ensure-stripe"
          className="btn-pill btn-primary w-full !text-xs disabled:opacity-60 mt-2"
          title="Creates any missing Stripe Prices (deposits + pay-in-full) using the amounts stored here"
        >
          <Plus size={12} className={ensuring ? "animate-pulse" : ""} />
          {ensuring ? "Creating in Stripe…" : "Create missing prices in Stripe"}
        </button>
      </div>

      <div className="lg:col-span-8">
        {creating ? (
          <CourseCreateForm onCreated={(slug) => { setCreating(false); setSelectedSlug(slug); load(); }} onCancel={() => setCreating(false)} />
        ) : selected ? (
          <CourseFormPanel key={selected.slug} course={selected} onSaved={load} />
        ) : (
          <div className="rounded-3xl bg-white border border-dashed border-[hsl(var(--onyx)/0.15)] p-10 text-center text-[hsl(var(--onyx)/0.5)]">
            Pick a course to start editing.
          </div>
        )}
      </div>
    </div>
  );
}

function CourseCreateForm({ onCreated, onCancel }) {
  const [form, setForm] = useState({
    slug: "", name: "", tagline: "", duration: "", who_for: "",
    certification: "", sort_order: 100, accredited: false, poster_image: false, hidden: false,
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!/^[a-z0-9-]+$/.test(form.slug)) return toast.error("Slug must be lowercase letters/numbers/hyphens");
    setBusy(true);
    try {
      await axios.post(`${API}/admin/courses`, {
        ...form,
        about: [], what_you_learn: [], whats_included: [],
      });
      toast.success("Course created — add copy & pricing next.");
      onCreated(form.slug);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create course");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-4" data-testid="content-create-form">
      <div className="flex items-center justify-between">
        <div className="overline">New course</div>
        <button type="button" onClick={onCancel} className="text-xs text-[hsl(var(--onyx)/0.55)] hover:text-[hsl(var(--onyx))]">
          Cancel
        </button>
      </div>
      <LabeledInput label="Slug (URL-safe, no spaces)" testid="new-slug" required value={form.slug} onChange={(v) => setForm({ ...form, slug: v.toLowerCase() })} placeholder="new-course-slug" />
      <LabeledInput label="Name" testid="new-name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
      <LabeledInput label="Tagline" testid="new-tagline" value={form.tagline} onChange={(v) => setForm({ ...form, tagline: v })} />
      <div className="grid gap-3 sm:grid-cols-2">
        <LabeledInput label="Duration" value={form.duration} onChange={(v) => setForm({ ...form, duration: v })} placeholder="1 day · 6 hours" />
        <LabeledInput label="Who for" value={form.who_for} onChange={(v) => setForm({ ...form, who_for: v })} />
      </div>
      <LabeledInput label="Certification" value={form.certification} onChange={(v) => setForm({ ...form, certification: v })} />
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={form.accredited} onChange={(e) => setForm({ ...form, accredited: e.target.checked })} /> Accredited</label>
        <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={form.poster_image} onChange={(e) => setForm({ ...form, poster_image: e.target.checked })} /> Poster-style image</label>
        <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={form.hidden} onChange={(e) => setForm({ ...form, hidden: e.target.checked })} /> Hidden</label>
      </div>
      <button type="submit" disabled={busy} data-testid="content-create-submit" className="btn-pill btn-primary disabled:opacity-60">
        {busy ? "Creating…" : "Create course"}
      </button>
    </form>
  );
}

function CourseFormPanel({ course, onSaved }) {
  const [form, setForm] = useState(course);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const dirty = JSON.stringify(form) !== JSON.stringify(course);

  useEffect(() => { setForm(course); }, [course]);

  const save = async () => {
    setSaving(true);
    try {
      // Only send editable fields — never slug/lookup_keys/created_at
      const payload = {
        name: form.name, tagline: form.tagline, duration: form.duration,
        who_for: form.who_for, certification: form.certification, image: form.image,
        poster_image: !!form.poster_image, accredited: !!form.accredited,
        hidden: !!form.hidden, sort_order: Number(form.sort_order) || 100,
        about: form.about || [], what_you_learn: form.what_you_learn || [],
        whats_included: form.whats_included || [],
      };
      await axios.patch(`${API}/admin/courses/${course.slug}`, payload);
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const uploadImage = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const file = files[0];
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const r = await axios.post(`${API}/admin/courses/${course.slug}/image`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image: r.data.image }));
      toast.success("Image uploaded");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Image upload failed");
    } finally { setUploading(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete course "${course.name}"? This will also archive its Stripe prices.`)) return;
    try {
      await axios.delete(`${API}/admin/courses/${course.slug}`);
      toast.success("Course deleted");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid={`content-edit-${course.slug}`}>
      <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="overline">Editing</div>
            <h3 className="font-display text-2xl mt-1 tracking-tight">{course.name}</h3>
            <div className="text-xs text-[hsl(var(--onyx)/0.5)] mt-1">{course.slug}</div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={save}
              disabled={saving || !dirty}
              data-testid="content-save"
              className="btn-pill btn-primary disabled:opacity-50"
            >
              <Save size={14} /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
            </button>
            <button
              onClick={remove}
              data-testid="content-delete"
              className="btn-pill btn-outline"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-4">
        <div className="overline">Cover image</div>
        <div className="grid gap-4 sm:grid-cols-[240px_1fr] items-start">
          <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-blush/40 border border-[hsl(var(--onyx)/0.08)]">
            {form.image ? (
              <img src={form.image} alt={form.name} className={form.poster_image ? "w-full h-full object-contain" : "w-full h-full object-cover"} />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-xs text-[hsl(var(--onyx)/0.45)]">No image yet</div>
            )}
          </div>
          <div className="space-y-3">
            <label
              htmlFor={`course-file-${course.slug}`}
              data-testid="course-image-uploader"
              className={`block w-full text-center py-6 rounded-2xl border-2 border-dashed cursor-pointer transition-colors ${uploading ? "border-[hsl(var(--onyx)/0.1)] text-[hsl(var(--onyx)/0.35)]" : "border-[hsl(var(--onyx)/0.2)] text-[hsl(var(--onyx)/0.7)] hover:border-[hsl(var(--onyx))] hover:text-[hsl(var(--onyx))]"}`}
            >
              {uploading ? "Uploading…" : (
                <><Upload size={16} className="inline mr-2" /> Upload from device</>
              )}
              <div className="text-[11px] mt-1 text-[hsl(var(--onyx)/0.5)]">JPG · PNG · WebP · HEIC · up to 12 MB</div>
            </label>
            <input
              id={`course-file-${course.slug}`}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
              disabled={uploading}
              onChange={(e) => { uploadImage(e.target.files); e.target.value = ""; }}
              className="hidden"
            />
            <label className="text-xs flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!form.poster_image}
                onChange={(e) => setForm({ ...form, poster_image: e.target.checked })}
              />
              Poster-style image (fits inside a pink background instead of filling the card)
            </label>
          </div>
        </div>
      </div>

      <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-4">
        <div className="overline">Basics</div>
        <LabeledInput label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="edit-name" />
        <LabeledTextarea label="Tagline (short pitch, ~1 sentence)" value={form.tagline || ""} onChange={(v) => setForm({ ...form, tagline: v })} rows={2} testid="edit-tagline" />
        <div className="grid gap-3 sm:grid-cols-2">
          <LabeledInput label="Duration" value={form.duration || ""} onChange={(v) => setForm({ ...form, duration: v })} placeholder="1 day · 6 hours" />
          <LabeledInput label="Who for" value={form.who_for || ""} onChange={(v) => setForm({ ...form, who_for: v })} />
        </div>
        <LabeledInput label="Certification" value={form.certification || ""} onChange={(v) => setForm({ ...form, certification: v })} />
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={!!form.accredited} onChange={(e) => setForm({ ...form, accredited: e.target.checked })} /> Accredited</label>
          <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={!!form.hidden} onChange={(e) => setForm({ ...form, hidden: e.target.checked })} /> Hidden from site</label>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">Sort order</label>
            <input type="number" value={form.sort_order ?? 100} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} className="w-full h-9 mt-1 rounded-xl border border-[hsl(var(--onyx)/0.15)] px-3 text-sm" />
          </div>
        </div>
      </div>

      <StringListEditor
        title="About the programme"
        subtitle="Long-form paragraphs shown on the course detail page. One paragraph per row."
        items={form.about || []}
        onChange={(list) => setForm({ ...form, about: list })}
        placeholder="Add a paragraph…"
        testidPrefix="edit-about"
      />

      <StringListEditor
        title="What you'll learn"
        subtitle="Short bullet points shown on the course card + detail page."
        items={form.what_you_learn || []}
        onChange={(list) => setForm({ ...form, what_you_learn: list })}
        placeholder="Add a learning point…"
        testidPrefix="edit-learn"
      />

      <StringListEditor
        title="What's included"
        subtitle="Kit, certification, ongoing support, etc."
        items={form.whats_included || []}
        onChange={(list) => setForm({ ...form, whats_included: list })}
        placeholder="Add an included item…"
        testidPrefix="edit-included"
      />

      <PricingPanel course={course} onSaved={onSaved} />
    </div>
  );
}

function PricingPanel({ course, onSaved }) {
  const [adding, setAdding] = useState(false);
  const [newTier, setNewTier] = useState({ label: "Pay in full", lookup_key: "", amount: "", note: "" });
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!newTier.label || !newTier.lookup_key || !newTier.amount) return toast.error("Fill label, key & amount");
    if (!/^[a-z0-9_-]+$/.test(newTier.lookup_key)) return toast.error("Lookup key must be lowercase letters/numbers/_ /-");
    if (!window.confirm(`Create a new Stripe Price of £${Number(newTier.amount).toFixed(2)}? This will be live in Stripe immediately.`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/courses/${course.slug}/pricing`, {
        label: newTier.label,
        lookup_key: newTier.lookup_key,
        amount: Number(newTier.amount),
        note: newTier.note || null,
      });
      toast.success("Pay tier added");
      setAdding(false);
      setNewTier({ label: "Pay in full", lookup_key: "", amount: "", note: "" });
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add price");
    } finally { setBusy(false); }
  };

  const updateAmount = async (lk, newAmount) => {
    if (!newAmount || Number(newAmount) === Number(lk.amount)) return;
    if (!window.confirm(`Change £${lk.amount} → £${Number(newAmount).toFixed(2)}? A new Stripe Price will be created and the old one archived. This affects LIVE checkouts.`)) return;
    try {
      await axios.patch(`${API}/admin/courses/${course.slug}/pricing/${lk.lookup_key}`, { amount: Number(newAmount) });
      toast.success("Price updated in Stripe");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (lk) => {
    if (!window.confirm(`Remove pay tier "${lk.label}" (£${lk.amount})? The Stripe Price will be archived.`)) return;
    try {
      await axios.delete(`${API}/admin/courses/${course.slug}/pricing/${lk.lookup_key}`);
      toast.success("Pay tier removed");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    }
  };

  return (
    <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-4" data-testid="content-pricing">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline">Pay tiers</div>
          <p className="text-xs text-[hsl(var(--onyx)/0.6)] mt-1 max-w-md">
            Changes here push straight to <strong>live Stripe</strong>. New tiers create new Prices. Amount edits transfer the lookup_key onto a new Price and archive the old one.
          </p>
        </div>
        <button
          onClick={() => setAdding((a) => !a)}
          data-testid="pricing-add-toggle"
          className="btn-pill btn-outline !text-xs"
        >
          <Plus size={12} /> Add tier
        </button>
      </div>

      {adding && (
        <div className="rounded-2xl border border-[hsl(var(--onyx)/0.1)] bg-blush/30 p-4 space-y-3" data-testid="pricing-add-form">
          <div className="grid gap-3 sm:grid-cols-2">
            <LabeledInput label="Label" value={newTier.label} onChange={(v) => setNewTier({ ...newTier, label: v })} placeholder="Pay in full" />
            <LabeledInput label="Lookup key (Stripe)" value={newTier.lookup_key} onChange={(v) => setNewTier({ ...newTier, lookup_key: v.toLowerCase() })} placeholder="course_slug_full" />
            <LabeledInput label="Amount (£)" value={newTier.amount} onChange={(v) => setNewTier({ ...newTier, amount: v })} placeholder="250" />
            <LabeledInput label="Note (optional)" value={newTier.note} onChange={(v) => setNewTier({ ...newTier, note: v })} placeholder="All-in — kit included" />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setAdding(false)} className="btn-pill btn-outline !text-xs flex-1">Cancel</button>
            <button type="button" onClick={add} disabled={busy} data-testid="pricing-add-submit" className="btn-pill btn-primary !text-xs flex-1 disabled:opacity-60">
              {busy ? "Creating in Stripe…" : "Add & push to Stripe"}
            </button>
          </div>
        </div>
      )}

      {(course.lookup_keys || []).length === 0 ? (
        <div className="rounded-2xl bg-blush/20 border border-dashed border-[hsl(var(--onyx)/0.15)] p-6 text-center text-sm text-[hsl(var(--onyx)/0.55)]">
          No pay tiers yet — the course won't be purchasable until you add one.
        </div>
      ) : (
        <div className="space-y-2">
          {(course.lookup_keys || []).map((lk) => (
            <PricingRow key={lk.lookup_key} lk={lk} onUpdate={updateAmount} onRemove={remove} />
          ))}
        </div>
      )}
    </div>
  );
}

function PricingRow({ lk, onUpdate, onRemove }) {
  const [amount, setAmount] = useState(String(lk.amount));
  useEffect(() => { setAmount(String(lk.amount)); }, [lk.amount]);
  return (
    <div className="rounded-2xl border border-[hsl(var(--onyx)/0.06)] p-4 flex flex-wrap items-center gap-3" data-testid={`pricing-row-${lk.lookup_key}`}>
      <div className="flex-1 min-w-[180px]">
        <div className="font-medium text-[hsl(var(--onyx))]">{lk.label}</div>
        <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.5)]">{lk.lookup_key}</div>
        {lk.note && <div className="text-xs text-[hsl(var(--onyx)/0.6)] mt-1">{lk.note}</div>}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-lg">£</span>
        <input
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          data-testid={`pricing-amount-${lk.lookup_key}`}
          className="w-24 h-9 rounded-xl border border-[hsl(var(--onyx)/0.15)] px-2 text-sm text-right"
        />
        <button
          onClick={() => onUpdate(lk, amount)}
          disabled={Number(amount) === Number(lk.amount)}
          data-testid={`pricing-save-${lk.lookup_key}`}
          className="btn-pill btn-outline !text-xs !py-1.5 disabled:opacity-40"
        >
          Update
        </button>
      </div>
      <button
        onClick={() => onRemove(lk)}
        data-testid={`pricing-remove-${lk.lookup_key}`}
        className="btn-pill btn-outline !text-xs !py-1.5"
      >
        <Trash2 size={12} />
      </button>
    </div>
  );
}

// ————— Small reusable inputs —————
function LabeledInput({ label, value, onChange, placeholder, testid, required }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">{label}</label>
      <input
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        data-testid={testid}
        className="w-full h-11 mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
      />
    </div>
  );
}

function LabeledTextarea({ label, value, onChange, rows = 3, placeholder, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[hsl(var(--onyx)/0.55)]">{label}</label>
      <textarea
        value={value ?? ""}
        rows={rows}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full mt-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 py-2 text-sm outline-none focus:border-[hsl(var(--onyx))] resize-y"
      />
    </div>
  );
}

function StringListEditor({ title, subtitle, items, onChange, placeholder, testidPrefix }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    if (!draft.trim()) return;
    onChange([...(items || []), draft.trim()]);
    setDraft("");
  };
  const update = (i, val) => {
    const next = [...(items || [])];
    next[i] = val;
    onChange(next);
  };
  const remove = (i) => onChange((items || []).filter((_, j) => j !== i));
  const move = (i, dir) => {
    const next = [...(items || [])];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-3" data-testid={`${testidPrefix}-panel`}>
      <div>
        <div className="overline">{title}</div>
        <p className="text-xs text-[hsl(var(--onyx)/0.55)] mt-1">{subtitle}</p>
      </div>
      <div className="space-y-2">
        {(items || []).length === 0 && (
          <div className="text-xs text-[hsl(var(--onyx)/0.45)] italic">Nothing added yet.</div>
        )}
        {(items || []).map((item, i) => (
          <div key={i} className="flex items-start gap-2" data-testid={`${testidPrefix}-row-${i}`}>
            <textarea
              value={item}
              rows={Math.max(1, Math.ceil((item || "").length / 100))}
              onChange={(e) => update(i, e.target.value)}
              className="flex-1 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 py-2 text-sm outline-none focus:border-[hsl(var(--onyx))] resize-y"
            />
            <div className="flex flex-col gap-1">
              <button onClick={() => move(i, -1)} disabled={i === 0} type="button" className="text-xs px-2 py-1 rounded-lg border border-[hsl(var(--onyx)/0.15)] disabled:opacity-30">↑</button>
              <button onClick={() => move(i, 1)} disabled={i === (items.length - 1)} type="button" className="text-xs px-2 py-1 rounded-lg border border-[hsl(var(--onyx)/0.15)] disabled:opacity-30">↓</button>
              <button onClick={() => remove(i)} type="button" data-testid={`${testidPrefix}-remove-${i}`} className="text-xs px-2 py-1 rounded-lg border border-[hsl(var(--onyx)/0.15)] text-[hsl(var(--burgundy))]"><X size={12} /></button>
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          data-testid={`${testidPrefix}-input`}
          className="flex-1 h-10 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
        />
        <button type="button" onClick={add} data-testid={`${testidPrefix}-add`} className="btn-pill btn-outline !text-xs">
          <Plus size={12} /> Add
        </button>
      </div>
    </div>
  );
}
